type BodyStyleSnapshot = {
  overflow: string;
  position: string;
  top: string;
  width: string;
  touchAction: string;
};

/**
 * body scroll-lock ref-count 코어 (#184). 오버레이가 중첩 열릴 때 각자 body 를 잠그면 안쪽이 바깥의
 * scrollY 를 덮어써 최종 복원 위치가 어긋난다 — 그래서 카운터 + 단일 base snapshot 으로 통합한다.
 *
 * 잠금 방식은 하나(position:fixed)다 — 팝업·시트·오버레이 모두 이것을 쓴다(B2 결정 2026-10-27, #457).
 * - body 는 `scrollY − visualViewport.offsetTop` 만큼만 올려 보이는 자리를 지킨다(#457 측정 기준):
 *   iOS 시뮬 Safari 에서 키보드를 띄우면 scrollY·offsetTop 이 함께 131 이었고, -scrollY 로 올리면 내용이 131px 더
 *   올라갔다(입력칸 176→45). Chromium 에서 문서를 150px 스크롤한 상태는 offsetTop 0 이었고, -150 으로 올려야
 *   제자리였다(0 이면 150px 내려옴).
 * - `lockCount` 모듈 레벨 카운터로 활성 락 수를 추적한다.
 * - base snapshot(원본 overflow/position/top/width/touchAction)과 `savedScrollY` 는
 *   카운트가 0→1 이 되는 최초 1회에만 캡처하고, 이후 재캡처·덮어쓰기하지 않는다.
 * - 실제 body 복원(+ `scrollTo`)은 카운트가 마지막으로 0 에 도달할 때 딱 한 번만 실행된다.
 * - `unlock` 은 카운트가 이미 0 이면 no-op (음수 카운트 금지).
 * - `typeof document === "undefined"` (SSR 등)면 lock/unlock 모두 no-op.
 */

let lockCount = 0;
let baseSnapshot: BodyStyleSnapshot | null = null;
let savedScrollY = 0;
let savedTop = 0;

const readBaseSnapshot = (): BodyStyleSnapshot => ({
  overflow: document.body.style.overflow,
  position: document.body.style.position,
  top: document.body.style.top,
  width: document.body.style.width,
  touchAction: document.body.style.touchAction,
});

const applyFixedLock = (): void => {
  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.top = `-${savedTop}px`;
  document.body.style.width = "100%";
  document.body.style.touchAction = "none";
};

const restoreBaseSnapshot = (snapshot: BodyStyleSnapshot): void => {
  document.body.style.overflow = snapshot.overflow;
  document.body.style.position = snapshot.position;
  document.body.style.top = snapshot.top;
  document.body.style.width = snapshot.width;
  document.body.style.touchAction = snapshot.touchAction;
  window.scrollTo(0, savedScrollY);
};

/** body scroll 을 잠근다. 카운트가 올라가며, 여러 오버레이가 중첩돼도 안전하다. */
export const lockBodyScroll = (): void => {
  if (typeof document === "undefined") return;

  if (lockCount === 0) {
    baseSnapshot = readBaseSnapshot();
    savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    // visualViewport.offsetTop 만큼은 빼고 올린다 — 위 주석(측정 근거).
    savedTop = Math.max(0, Math.round(savedScrollY - (window.visualViewport?.offsetTop ?? 0)));
  }

  lockCount += 1;
  applyFixedLock();
};

/** `lockBodyScroll` 과 짝을 이루는 해제. 카운트가 이미 0이면 no-op. */
export const unlockBodyScroll = (): void => {
  if (typeof document === "undefined") return;
  if (lockCount === 0) return;

  lockCount -= 1;
  // 남은 잠금이 있으면 고정 값을 다시 쓴다(#184 의 매 호출 재계산과 같은 결과).
  if (lockCount > 0) {
    applyFixedLock();
    return;
  }

  if (baseSnapshot) restoreBaseSnapshot(baseSnapshot);
  baseSnapshot = null;
  savedScrollY = 0;
  savedTop = 0;
};
